
import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const ColdOutreach = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Cold Email & Calling Basics Training | BPO Consultants"
                description="Conquer cold calling anxiety. Learn proven scripts for phone and email outreach that get replies."
                keywords="Cold Calling Training, Cold Email Templates, SDR Skills, Outbound Sales, Sales Scripts"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1523966211575-eb4a01e7dd51?auto=format&fit=crop&q=80&w=1200" alt="Cold Calling" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Phone size={14} className="inline mr-1" /> Module 2: Prospecting
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Cold Email & <br /> Calling Basics
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            The phone isn't dead. You just need to know what to say.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">Breaking the Ice</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            Outbound prospecting is the lifeblood of B2B sales. This module gives you the tactical scripts and frameworks to interrupt a prospect's day and earn 30 seconds of their time.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "The Permission Slap",
                                    desc: "Why asking 'Is now a bad time?' actually increases your conversion rate by 40%."
                                },
                                {
                                    title: "Email Subject Lines",
                                    desc: "How to write subject lines that get opened, avoiding spam triggers and 'salesy' language."
                                },
                                {
                                    title: "The 30-Second Commercial",
                                    desc: "Crafting a pitch that focuses on the prospect's problems, not your product features."
                                },
                                {
                                    title: "Voicemail Strategy",
                                    desc: "Leaving voicemails that actually get callbacks (hint: keep it under 20 seconds)."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <Mail size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    <FadeIn className="mb-16 bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-2xl text-white">
                        <h3 className="text-2xl font-bold mb-4">The Fear of Rejection</h3>
                        <p className="text-gray-300 italic mb-4">
                            "We tackle 'Phone Fear' head-on. You will learn to detach your self-worth from the outcome of the call. 'No' is just data."
                        </p>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Start Dialing</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default ColdOutreach;
